import { IonAlert, IonicSafeString, IonLoading } from "@ionic/react";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { $api } from "@/api/api";
import { TEN_SECONDS } from "@/utils/constants/time";

import { ApiTokenContext } from "../api-token/ApiContextWrapper";
import { UserContext } from "../user/UserContext";

import { AuthApiContext } from "./AuthApiProvider";

// This provider shortcuts children components, if no api is available
const FixedAuthContext = createContext<{
  $authApi: typeof $api;
}>({
  $authApi: $api,
});

const ALERT_LOGOUT_MESSAGE = new IonicSafeString(
  `<ion-row class="ion-align-items-center">
  <ion-col size="auto">You can wait here or logout and try again.</ion-col>
  <ion-col size="auto"><ion-spinner></ion-spinner></ion-col>
  </ion-row>
  `,
);

function AuthLoading() {
  const { logout } = useContext(ApiTokenContext);
  const [timeout, setTimeout] = useState<boolean>(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeout(true);
    }, TEN_SECONDS);
    return () => clearInterval(interval);
  }, [timeout]);
  useEffect(() => () => setTimeout(false), [setTimeout]);
  if (timeout) {
    return (
      <IonAlert
        isOpen={true}
        header="Loading..."
        message={ALERT_LOGOUT_MESSAGE}
        onDidDismiss={() => {
          setTimeout(false);
        }}
        buttons={[
          {
            handler: () => logout(),
            role: "confirm",
            text: "Logout",
          },
        ]}
      />
    );
  }
  return <IonLoading isOpen={true} message="Loading..." />;
}

function ApiOrLoadingProvider({ children }: { children: ReactNode }) {
  const { $authApi } = useContext(AuthApiContext);
  const { user } = useContext(UserContext);

  const canLoad = user && $authApi;

  return (
    <FixedAuthContext.Provider value={{ $authApi: $authApi ?? $api }}>
      {canLoad ? children : <AuthLoading />}
    </FixedAuthContext.Provider>
  );
}

function UserOrLoadingWrapper({ children }: { children: ReactNode }) {
  const { user } = useContext(UserContext);
  return <>{user?.ID ? children : <AuthLoading />}</>;
}

function useFixedAuthApi() {
  const { $authApi } = useContext(FixedAuthContext);
  return $authApi;
}

function useFixedUser() {
  const { user } = useContext(UserContext);
  if (!user) {
    throw new Error("User not found");
  }
  return user;
}

export {
  ApiOrLoadingProvider,
  FixedAuthContext,
  useFixedAuthApi,
  useFixedUser,
  UserOrLoadingWrapper,
};
